import { useState, useMemo, useRef, useEffect } from 'react'
import type { InventoryCard } from '../../types/inventory'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Loader2, Plus, Search, X } from 'lucide-react'

interface AddCardProps {
  availableCards: InventoryCard[]
  onAdd: (cardId: string, price: number) => Promise<void>
}

function getCardDisplayName(card: InventoryCard): string {
  const name = card.card?.name ?? card.cardId
  const setName = card.card?.set?.name
  return setName ? `${name} (${setName})` : name
}

function getCardMeta(card: InventoryCard): string {
  const parts: string[] = []
  if (card.condition) parts.push(card.condition)
  if (card.storageLocation?.name?.trim()) parts.push(card.storageLocation.name.trim())
  return parts.length > 0 ? parts.join(' · ') : ''
}

function matchesSearch(card: InventoryCard, query: string): boolean {
  if (!query.trim()) return true
  const q = query.toLowerCase().trim()
  const name = card.card?.name?.toLowerCase() ?? ''
  const setName = card.card?.set?.name?.toLowerCase() ?? ''
  const cardId = card.cardId.toLowerCase()
  return name.includes(q) || setName.includes(q) || cardId.includes(q)
}

export function AddCard({ availableCards, onAdd }: AddCardProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [addPrice, setAddPrice] = useState<string>('0')
  const [isAdding, setIsAdding] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const filteredCards = useMemo(() => {
    return availableCards.filter((c) => matchesSearch(c, searchQuery))
  }, [availableCards, searchQuery])

  const selectedCard = selectedCardId
    ? availableCards.find((c) => c.id === selectedCardId)
    : null

  useEffect(() => {
    setHighlightedIndex(0)
  }, [searchQuery, filteredCards.length])

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement
      el?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  const handleBlur = () => {
    setTimeout(() => setIsOpen(false), 150)
  }

  const handleSelect = (card: InventoryCard) => {
    setSelectedCardId(card.id)
    setAddPrice(card.priceWhenAdded != null ? String(card.priceWhenAdded) : '0')
    setSearchQuery('')
    setIsOpen(false)
  }

  const handleClear = () => {
    setSelectedCardId(null)
    setSearchQuery('')
    setAddPrice('0')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && e.key !== 'Escape') {
      setIsOpen(true)
    }
    if (e.key === 'Escape') {
      setIsOpen(false)
      if (selectedCardId) handleClear()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.min(i + 1, filteredCards.length - 1))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.max(i - 1, 0))
      return
    }
    if (e.key === 'Enter' && filteredCards.length > 0) {
      e.preventDefault()
      handleSelect(filteredCards[highlightedIndex])
      return
    }
  }

  const handleAdd = async () => {
    if (!selectedCardId) return
    const price = parseFloat(addPrice)
    if (isNaN(price) || price < 0) {
      alert('Please enter a valid price')
      return
    }
    setIsAdding(true)
    try {
      await onAdd(selectedCardId, price)
      handleClear()
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <Label className="text-gray-700 mb-2 block">Add card</Label>
      <div className="flex flex-wrap gap-2 items-end">
        <div ref={containerRef} className="relative w-[280px]">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              value={selectedCard ? getCardDisplayName(selectedCard) : searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setSelectedCardId(null)
                setIsOpen(true)
              }}
              onFocus={() => {
                if (selectedCard) handleClear()
                setIsOpen(true)
              }}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              placeholder="Search cards..."
              className="pl-8 pr-8 bg-white border-gray-300"
              readOnly={!!selectedCard}
            />
            {selectedCard ? (
              <button
                type="button"
                onClick={handleClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-200 text-muted-foreground"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          {isOpen && (
            <div
              className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-60 overflow-auto"
              onMouseDown={(e) => e.preventDefault()}
            >
              <div ref={listRef}>
                {filteredCards.length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No cards match &quot;{searchQuery}&quot;
                  </div>
                ) : (
                  filteredCards.map((card, i) => {
                    const meta = getCardMeta(card)
                    return (
                      <button
                        key={card.id}
                        type="button"
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                          i === highlightedIndex ? 'bg-gray-100' : ''
                        }`}
                        onMouseDown={() => handleSelect(card)}
                        onMouseEnter={() => setHighlightedIndex(i)}
                      >
                        <div className="font-medium">{getCardDisplayName(card)}</div>
                        {meta ? (
                          <div className="text-xs text-muted-foreground mt-0.5">{meta}</div>
                        ) : null}
                      </button>
                    )
                  })
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground text-sm">$</span>
          <Input
            type="text"
            value={addPrice}
            onChange={(e) => {
              const v = e.target.value
              if (v === '' || /^\d*\.?\d*$/.test(v)) setAddPrice(v)
            }}
            placeholder="0.00"
            className="w-20 h-9 text-sm"
          />
        </div>
        <Button
          onClick={handleAdd}
          disabled={!selectedCardId || isAdding}
          className="bg-blue-800 text-white hover:bg-blue-900"
        >
          {isAdding ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Plus className="h-4 w-4 mr-1" />
              Add
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
